import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, GraduationCap, MapPin, Bus, BookOpen, AlertTriangle, DollarSign, Home, TrendingUp, BarChart3 } from 'lucide-react';
import { useEffect } from 'react';
import { trackEvent } from '../lib/api';
import SEO from '../components/SEO';

export function SchoolsPage() {
  useEffect(() => { trackEvent({ event_type: 'page_view', page: 'schools' }); }, []);

  return (
    <div className="min-h-screen pt-16" data-testid="schools-page">
      <SEO
        title="Burlington Schools Guide — Halton District School Board, Catholic & French Immersion"
        description="Burlington school guide for relocating families: Halton DSB rankings, Catholic schools, French immersion availability, and which neighbourhoods have the strongest school catchments."
        canonical="/schools"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Schools', url: '/schools' }]}
        article={{ dateModified: '2026-03-01' }}
      />
      {/* ─── Hero ─── */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/nfduamj0_shutterstock_1584051751.jpg" alt="Burlington aerial neighbourhoods" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600 mb-3 block">Burlington Schools</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4 max-w-3xl" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Schools are the #1 reason families <span className="italic">move here</span>
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl leading-relaxed mb-4">
            Burlington's Halton District School Board consistently ranks among the top in Ontario. But not every neighbourhood feeds into the same catchments — and that changes where you should be looking.
          </p>
          <p className="text-base font-medium text-slate-700 mb-8">
            Understand the school landscape before you tour a single home.
          </p>
          <Link to="/blueprint" data-testid="schools-hero-cta">
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-7 h-11 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all">
              Match Neighbourhoods to My School Priorities <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          <p className="text-xs text-slate-400 mt-3">Public, Catholic, or French Immersion — we factor it all in.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* ─── Why Schools Drive Relocation ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Why families leave Toronto <span className="italic">for Burlington schools</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed mb-6">
              Overcrowded classrooms, lottery-based admissions, and long waitlists for French immersion — Toronto and Mississauga parents are increasingly looking west. Burlington offers:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {[
                'Smaller class sizes in many schools',
                'Consistently high EQAO scores across Halton',
                'Shorter French immersion waitlists',
                'Community-based school zones (not lottery)',
                'Active parent involvement culture',
                'Strong extracurricular and athletics programs',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"></div>
                  <span className="text-sm text-slate-700">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-base text-slate-500 leading-relaxed">
              The difference isn't just rankings — it's the feel. Schools in Burlington tend to be neighbourhood-embedded, walkable, and less transient.
            </p>
          </div>
        </section>

        {/* ─── School Boards ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              The school boards <span className="italic">you need to know</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            {[
              {
                name: 'Halton District School Board',
                abbr: 'HDSB',
                type: 'Public',
                highlights: ['Top-performing board in Ontario', 'French immersion at select schools', 'Strong special education support'],
                accent: 'bg-emerald-500',
              },
              {
                name: 'Halton Catholic DSB',
                abbr: 'HCDSB',
                type: 'Catholic',
                highlights: ['Faith-based curriculum', 'Strong community involvement', 'Competitive academics'],
                accent: 'bg-blue-500',
              },
              {
                name: 'French Immersion',
                abbr: 'FI',
                type: 'Via HDSB',
                highlights: ['Available at select elementary schools', 'Early & late entry options', 'High demand — register early'],
                accent: 'bg-orange-500',
              },
            ].map((board, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-6 card-interactive">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-9 h-9 rounded-xl ${board.accent} flex items-center justify-center`}>
                    <GraduationCap className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{board.name}</p>
                    <p className="text-[10px] font-mono text-slate-400 uppercase tracking-wider">{board.type}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {board.highlights.map((h, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0"></div>
                      <span className="text-[13px] text-slate-600">{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── CTA 1 ─── */}
        <section className="py-12 border-b border-slate-100">
          <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Which neighbourhoods have the strongest school catchments?</h3>
              <p className="text-sm text-slate-500">Not all Burlington areas are created equal. The Blueprint factors in your school preferences.</p>
            </div>
            <Link to="/blueprint" className="flex-shrink-0" data-testid="schools-cta-1">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                Match My School Priorities <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* ─── School by Neighbourhood ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Schools vary <span className="italic">by neighbourhood</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed mb-8">
              This is where most relocating families get it wrong. They fall in love with a house, then discover the school catchment doesn't match what they expected.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {[
              { area: 'Alton Village / Orchard', strength: 'Top-rated public schools', detail: 'Alton Village PS, Orchard Park PS consistently rank high. French immersion available.', tag: 'schools_strong' },
              { area: 'Roseland / LaSalle', strength: 'Strong catchments, established schools', detail: 'Close to Nelson HS and Paul Fisher PS. Mature school communities with deep roots.', tag: 'schools_strong' },
              { area: 'Burlington East', strength: 'Multiple options, growing area', detail: 'Brant Hills PS well-regarded. French immersion at some locations. Catholic options.', tag: 'mixed' },
              { area: 'Freeman / Kilbride', strength: 'Growing infrastructure', detail: 'Newer schools being built in Freeman. Kilbride requires travel to central Burlington.', tag: 'developing' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-6">
                <p className="text-base font-medium text-slate-900 mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>{item.area}</p>
                <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-2">{item.strength}</p>
                <p className="text-[13px] text-slate-500 leading-relaxed">{item.detail}</p>
              </div>
            ))}
          </div>

          <div className="bg-amber-50/50 rounded-2xl p-6 border border-amber-100/50 max-w-3xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900 mb-1">Important: Boundaries change</p>
                <p className="text-[13px] text-slate-500 leading-relaxed">
                  School catchment boundaries are updated periodically. Always verify directly with HDSB or HCDSB before making a purchase decision based on school access. This guide is for general orientation only.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ─── CTA 2 ─── */}
        <section className="py-12 border-b border-slate-100">
          <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Have kids? Their ages change the recommendation.</h3>
              <p className="text-sm text-slate-500">Elementary vs. secondary catchments differ. Tell us your kids' ages and we'll factor it in.</p>
            </div>
            <Link to="/blueprint" className="flex-shrink-0" data-testid="schools-cta-2">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                Get Age-Specific Matches <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* ─── What to Consider ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              What smart families <span className="italic">check first</span>
            </h2>
            <div className="space-y-4 mb-8">
              {[
                { num: '01', title: 'Verify the catchment before you offer', text: 'A house one street over can feed into a completely different school. Don\'t assume — verify with the board.' },
                { num: '02', title: 'French immersion fills fast', text: 'If FI is a priority, register early and confirm availability at your target school. Wait lists are real.' },
                { num: '03', title: 'Check transportation eligibility', text: 'Bus service depends on distance from school. Some areas are within the "walk zone" — fine in September, less fun in January.' },
                { num: '04', title: 'Consider the full trajectory', text: 'Elementary school matters, but so does secondary. Make sure the high school catchment also aligns.' },
              ].map((item) => (
                <div key={item.num} className="flex gap-4 p-4 bg-white rounded-2xl border border-slate-200/80">
                  <span className="font-mono text-xs text-slate-400 mt-0.5">{item.num}</span>
                  <div>
                    <p className="text-sm font-medium text-slate-900 mb-1">{item.title}</p>
                    <p className="text-[13px] text-slate-500 leading-relaxed">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>

      {/* ─── Final CTA ─── */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="relative rounded-3xl overflow-hidden p-12 md:p-16">
            <div className="absolute inset-0">
              <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/yr1apojk_shutterstock_2665884737.jpg" alt="Burlington waterfront" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-slate-900/70" />
            </div>
            <div className="relative z-10 max-w-xl">
              <h2 className="text-3xl md:text-4xl text-white tracking-tight leading-[1.15] mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Don't choose the house first. <span className="italic">Choose the school zone.</span>
              </h2>
              <p className="text-base text-white/50 leading-relaxed mb-2">The Buyer Blueprint asks about your kids' ages, school preferences, and priorities — then matches you to the neighbourhoods that align.</p>
              <p className="text-sm text-white/30 mb-8">Under 3 minutes. Free. No account needed.</p>
              <Link to="/blueprint" data-testid="schools-cta">
                <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 py-3 h-auto text-sm font-semibold hover:-translate-y-px transition-all shadow-xl">
                  Find My School-Matched Neighbourhoods <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-sm text-slate-400">School data is for general guidance. Always verify catchment boundaries directly with HDSB or HCDSB.</p>
        </div>
      </section>
    </div>
  );
}

export function RelocationPage() {
  useEffect(() => { trackEvent({ event_type: 'page_view', page: 'relocation' }); }, []);

  return (
    <div className="min-h-screen pt-16" data-testid="relocation-page">
      <SEO
        title="Moving to Burlington from Toronto — Complete Relocation Guide for Families"
        description="Thinking about moving to Burlington, Ontario? Commute times, housing prices, school quality, neighbourhood comparisons, and lifestyle trade-offs — everything GTA families need to know before relocating."
        canonical="/relocation"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Relocation Guide', url: '/relocation' }]}
        article={{ dateModified: '2026-03-01' }}
      />
      {/* ─── Hero ─── */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/yr1apojk_shutterstock_2665884737.jpg" alt="Burlington waterfront pier at sunset" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600 mb-3 block">Moving to Burlington</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4 max-w-3xl" style={{ fontFamily: "'Instrument Serif', serif" }}>
            Thinking about moving to <span className="italic">Burlington?</span>
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl leading-relaxed mb-4">
            Before you browse listings, understand what life here actually looks like — commute, schools, housing, neighbourhoods, and the real trade-offs.
          </p>
          <p className="text-base font-medium text-slate-700 mb-8">
            Relocating is a life decision. Let's make it an informed one.
          </p>
          <Link to="/blueprint" data-testid="relocation-hero-cta">
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-7 h-11 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all">
              Build Your Personalized Relocation Plan <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          <p className="text-xs text-slate-400 mt-3">No pressure. No generic advice. Just clarity.</p>
        </div>
      </section>

      {/* ─── Content Sections ─── */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* ─── Why Burlington ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Why so many GTA families are <span className="italic">choosing Burlington</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed mb-6">
              Burlington has become the landing point for families who want:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {['More space', 'Stronger neighbourhood schools', 'A quieter pace', 'Access to nature', 'A manageable commute'].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"></div>
                  <span className="text-sm text-slate-700">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-base text-slate-500 leading-relaxed mb-4">
              With a population of ~190,000, Burlington offers suburban breathing room without feeling disconnected from urban life.
            </p>
            <p className="text-base text-slate-500 leading-relaxed">
              It consistently ranks among Canada's best places to live — not because it's trendy, but because it's stable, safe, and livable.
            </p>
          </div>
        </section>

        {/* ─── CTA 1 ─── */}
        <section className="py-12 border-b border-slate-100">
          <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Is Burlington right for you?</h3>
              <p className="text-sm text-slate-500">The answer depends on your commute tolerance, family needs, and budget. Instead of guessing...</p>
            </div>
            <Link to="/blueprint" className="flex-shrink-0" data-testid="relocation-cta-1">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                See Which Neighbourhoods Match <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* ─── Commute ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              The commute: <span className="italic">let's talk honestly</span>
            </h2>
            <p className="text-base text-slate-700 font-medium mb-8">This is the real question.</p>

            <div className="bento-grid grid-cols-1 md:grid-cols-2 rounded-2xl mb-8">
              <div className="p-6">
                <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">GO Train</p>
                <p className="text-base text-slate-700 font-medium mb-2">Burlington GO &rarr; Union Station</p>
                <p className="font-mono text-2xl font-semibold text-slate-900 mb-2">35–50 min</p>
                <p className="text-sm text-slate-500">Freeman and Aldershot are particularly strong for commuters.</p>
              </div>
              <div className="p-6">
                <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">Highway Access</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {['QEW', '403', '407 nearby'].map(h => (
                    <span key={h} className="font-mono text-xs bg-slate-50 border border-slate-200 text-slate-700 px-2.5 py-1 rounded-full">{h}</span>
                  ))}
                </div>
                <p className="text-sm text-slate-500">Morning traffic toward Toronto can be heavy. But for hybrid workers (2–3 days in office), Burlington often becomes the sweet spot.</p>
              </div>
            </div>

            <div className="bg-slate-900 rounded-2xl p-6 mb-6">
              <p className="text-base text-white leading-relaxed" style={{ fontFamily: "'Instrument Serif', serif" }}>
                The key question isn't "Is it farther?" It's: <span className="italic text-emerald-400">Is the lifestyle upgrade worth the commute adjustment?</span>
              </p>
              <p className="text-sm text-white/50 mt-2">For many families, the answer becomes yes.</p>
            </div>
          </div>
        </section>

        {/* ─── CTA 2 ─── */}
        <section className="py-12 border-b border-slate-100">
          <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Find the best neighbourhood for your commute style</h3>
              <p className="text-sm text-slate-500">GO train rider? Highway driver? Hybrid? We'll match you.</p>
            </div>
            <Link to="/blueprint" className="flex-shrink-0" data-testid="relocation-cta-2">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                Find My Commute Match <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* ─── Budget ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              What does your budget <span className="italic">really buy here?</span>
            </h2>
            <p className="text-base text-slate-700 font-medium mb-8">This is where expectations shift.</p>

            <div className="space-y-3 mb-8">
              {[
                { type: 'Condos', range: '~$450K – $900K' },
                { type: 'Townhomes', range: '~$750K – $1.3M' },
                { type: 'Detached', range: '~$950K – $2M+' },
                { type: 'Lakefront / luxury', range: '$3M+' },
              ].map((item) => (
                <div key={item.type} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                  <span className="text-sm text-slate-700 font-medium">{item.type}</span>
                  <span className="font-mono text-sm font-semibold text-slate-900">{item.range}</span>
                </div>
              ))}
            </div>

            <p className="text-base text-slate-500 leading-relaxed mb-4">
              Compared to Toronto, you typically gain: larger lots, driveways, finished basements, and family-oriented streets.
            </p>
            <p className="text-base text-slate-700 font-medium">
              You're not necessarily spending less. You're getting more for the same spend.
            </p>
          </div>
        </section>

        {/* ─── CTA 3 ─── */}
        <section className="py-12 border-b border-slate-100">
          <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Not sure if your budget is realistic?</h3>
              <p className="text-sm text-slate-500">Before you tour homes, understand what areas actually align with your price range.</p>
            </div>
            <Link to="/blueprint" className="flex-shrink-0" data-testid="relocation-cta-3">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                Check What My Budget Unlocks <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* ─── Neighbourhoods ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Neighbourhood differences matter <span className="italic">more than you think</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed mb-8">
              Burlington is not one uniform suburb. Each area feels different.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {[
              {
                label: 'Luxury Lakeside Living',
                areas: 'Roseland / Shoreacres',
                desc: 'Mature trees. Larger lots. Waterfront proximity.',
                accent: 'bg-amber-500',
              },
              {
                label: 'Family-Centric Newer Builds',
                areas: 'Millcroft / Orchard / Alton Village',
                desc: 'Quiet streets. School-focused. Subdivision-style planning.',
                accent: 'bg-emerald-500',
              },
              {
                label: 'Commuter-Friendly Mixed Housing',
                areas: 'Downtown / LaSalle / Freeman',
                desc: 'GO access. Condo options. Strong transit positioning.',
                accent: 'bg-blue-500',
              },
              {
                label: 'Value-Oriented Family Areas',
                areas: 'Burlington East / Tyandaga / Kilbride',
                desc: 'Solid homes. Strong community. Budget-conscious options.',
                accent: 'bg-orange-500',
              },
            ].map((cat, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-6 card-interactive">
                <div className={`w-2 h-2 rounded-full ${cat.accent} mb-3`}></div>
                <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-1">{cat.label}</p>
                <p className="text-base font-medium text-slate-900 mb-2" style={{ fontFamily: "'Instrument Serif', serif" }}>{cat.areas}</p>
                <p className="text-[13px] text-slate-500">{cat.desc}</p>
              </div>
            ))}
          </div>

          <p className="text-base text-slate-700 font-medium max-w-3xl">
            Choosing the right neighbourhood is often more important than choosing the right house.
          </p>
        </section>

        {/* ─── CTA 4 ─── */}
        <section className="py-12 border-b border-slate-100">
          <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Get matched with the 2–3 areas that fit</h3>
              <p className="text-sm text-slate-500">Based on your budget, timeline, family needs, and priorities.</p>
            </div>
            <Link to="/blueprint" className="flex-shrink-0" data-testid="relocation-cta-4">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                Get My Matches <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* ─── Schools ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Schools: one of the top <span className="italic">relocation drivers</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed mb-6">
              Many Toronto and Mississauga families relocate specifically for school access and less overcrowding. Burlington offers:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {['Strong public schools', 'Catholic options', 'French immersion programs', 'Community-based school zones'].map((item, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"></div>
                  <span className="text-sm text-slate-700">{item}</span>
                </div>
              ))}
            </div>
            <p className="text-base text-slate-500 leading-relaxed mb-4">
              But not every neighbourhood feeds into the same catchments. Relocating without understanding this can lead to regret.
            </p>
          </div>
        </section>

        {/* ─── CTA 5 ─── */}
        <section className="py-12 border-b border-slate-100">
          <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>See which neighbourhoods align with your school goals</h3>
              <p className="text-sm text-slate-500">Public, Catholic, or French Immersion — we factor it in.</p>
            </div>
            <Link to="/blueprint" className="flex-shrink-0" data-testid="relocation-cta-5">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                Match My School Priorities <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* ─── Lifestyle ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Lifestyle & recreation: <span className="italic">the emotional upgrade</span>
            </h2>
            <p className="text-base text-slate-700 font-medium mb-8">This is where Burlington wins.</p>
          </div>

          <div className="bento-grid grid-cols-1 md:grid-cols-3 rounded-2xl mb-8">
            <div className="p-6">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">Waterfront</p>
              <div className="space-y-1.5">
                {['Spencer Smith Park', 'Burlington Waterfront Trail', 'Marinas', 'Summer festivals'].map((item, i) => (
                  <p key={i} className="text-sm text-slate-700">{item}</p>
                ))}
              </div>
            </div>
            <div className="p-6">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">Nature</p>
              <div className="space-y-1.5">
                {['Royal Botanical Gardens', 'Mount Nemo', 'Niagara Escarpment trails'].map((item, i) => (
                  <p key={i} className="text-sm text-slate-700">{item}</p>
                ))}
              </div>
            </div>
            <div className="p-6">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">Community</p>
              <div className="space-y-1.5">
                {['Family-oriented culture', 'Active recreation programs', 'Strong minor sports leagues', 'Safe residential streets'].map((item, i) => (
                  <p key={i} className="text-sm text-slate-700">{item}</p>
                ))}
              </div>
            </div>
          </div>

          <p className="text-base text-slate-500 leading-relaxed max-w-3xl">
            You trade nightlife density for breathing room. For many families, that's not a sacrifice — it's the point.
          </p>
        </section>

        {/* ─── Who Thrives / Who May Struggle ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
            <div className="bg-emerald-50/50 rounded-2xl p-6 border border-emerald-100/50">
              <h3 className="text-xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>Who thrives in Burlington</h3>
              <div className="space-y-2.5">
                {['Families with young children', 'Hybrid commuters', 'Buyers upgrading from Toronto condos', 'Long-term stability seekers', 'Those prioritizing safety and space'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0"></div>
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-amber-50/50 rounded-2xl p-6 border border-amber-100/50">
              <h3 className="text-xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>Who may struggle</h3>
              <div className="space-y-2.5">
                {['5-day-a-week downtown commuters', 'Buyers seeking ultra-urban walkability', 'Nightlife-driven lifestyles'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0"></div>
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-[13px] text-slate-500 mt-4 italic">Being clear about fit avoids disappointment.</p>
            </div>
          </div>
        </section>

        {/* ─── The Smart Way ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              The smart way to <span className="italic">relocate</span>
            </h2>
            <p className="text-base text-slate-700 font-medium mb-2">Most buyers start with listings. That's backwards.</p>
            <p className="text-base text-slate-500 leading-relaxed mb-8">
              Smart relocation starts with:
            </p>
            <div className="space-y-4 mb-8">
              {[
                { num: '01', text: 'Clarity on commute trade-offs' },
                { num: '02', text: 'Understanding realistic budget bands' },
                { num: '03', text: 'Identifying 2–3 strong neighbourhood matches' },
                { num: '04', text: 'Aligning school and lifestyle priorities' },
              ].map((item) => (
                <div key={item.num} className="flex items-center gap-4">
                  <span className="font-mono text-xs text-slate-400">{item.num}</span>
                  <span className="text-base text-slate-700">{item.text}</span>
                </div>
              ))}
            </div>
            <p className="text-base text-slate-500 leading-relaxed">
              Once you know those, house hunting becomes efficient. Without that clarity, it becomes overwhelming.
            </p>
          </div>
        </section>

      </div>

      {/* ─── Final CTA ─── */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="relative rounded-3xl overflow-hidden p-12 md:p-16">
            <div className="absolute inset-0">
              <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/nfduamj0_shutterstock_1584051751.jpg" alt="Burlington neighbourhoods aerial" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-slate-900/75" />
            </div>
            <div className="relative z-10 max-w-xl">
              <h2 className="text-3xl md:text-4xl text-white tracking-tight leading-[1.15] mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Build your Burlington <span className="italic">relocation plan</span>
              </h2>
              <p className="text-base text-white/60 leading-relaxed mb-2">In under 3 minutes, you'll get:</p>
              <div className="space-y-2 mb-8">
                {[
                  'Your top neighbourhood matches',
                  'Budget alignment insights',
                  'School and commute considerations',
                  'Clear next steps tailored to you',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></div>
                    <span className="text-sm text-white/70">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-white/30 mb-6">No spam. No hard sell. Just structured guidance.</p>
              <Link to="/blueprint" data-testid="relocation-cta">
                <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 py-3 h-auto text-sm font-semibold hover:-translate-y-px transition-all shadow-xl">
                  Start My Burlington Relocation Plan <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Trust Footer ─── */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-sm text-slate-400">Helping families relocate to Burlington with clarity and confidence.</p>
          <p className="text-sm text-slate-400 italic">We focus on alignment first — property second.</p>
        </div>
      </section>
    </div>
  );
}

export function BudgetPage() {
  useEffect(() => { trackEvent({ event_type: 'page_view', page: 'budget' }); }, []);

  return (
    <div className="min-h-screen pt-16" data-testid="budget-page">
      <SEO
        title="Burlington Home Prices — What Your Budget Actually Buys by Neighbourhood"
        description="Burlington real estate prices by neighbourhood and property type. Condos from $425K, townhomes from $700K, detached from $950K. See what each price band gets you and where."
        canonical="/budget"
        breadcrumbs={[{ name: 'Home', url: '/' }, { name: 'Budget Planner', url: '/budget' }]}
        article={{ dateModified: '2026-03-01' }}
      />
      {/* ─── Hero ─── */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/swd0mu3s_shutterstock_2675683289.jpg" alt="Burlington residential area" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/50" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
          <span className="text-xs font-semibold tracking-widest uppercase text-emerald-600 mb-3 block">Burlington Pricing</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4 max-w-3xl" style={{ fontFamily: "'Instrument Serif', serif" }}>
            What does your budget <span className="italic">actually buy</span> in Burlington?
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl leading-relaxed mb-4">
            Burlington isn't cheap — but you typically get more for your money than Toronto or Mississauga. The question isn't just "Can I afford it?" It's "Which areas match my budget and my lifestyle?"
          </p>
          <p className="text-base font-medium text-slate-700 mb-8">
            Let's get specific.
          </p>
          <Link to="/blueprint" data-testid="budget-hero-cta">
            <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-7 h-11 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all">
              See What My Budget Unlocks <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
          <p className="text-xs text-slate-400 mt-3">Set your actual min–max and see which neighbourhoods align.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* ─── The Reality ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              The honest <span className="italic">pricing picture</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed mb-4">
              Burlington's real estate market spans from entry-level condos to multi-million dollar lakefront estates. The range is wider than most people expect.
            </p>
            <p className="text-base text-slate-700 font-medium mb-8">
              You're not necessarily spending less than Toronto. You're getting more for the same spend.
            </p>
          </div>

          <div className="space-y-1 mb-6">
            {[
              { type: 'Condos', range: '$450K – $900K', desc: 'Downtown, Burlington East, Tyandaga', icon: '🏢', width: '30%' },
              { type: 'Townhomes', range: '$700K – $1.3M', desc: 'Burlington East, Millcroft, Alton Village, LaSalle', icon: '🏘', width: '50%' },
              { type: 'Detached', range: '$850K – $2M+', desc: 'All neighbourhoods — varies dramatically by area', icon: '🏠', width: '75%' },
              { type: 'Premium / Lakefront', range: '$1.8M – $4.5M+', desc: 'Roseland, Shoreacres — limited inventory', icon: '🏡', width: '95%' },
            ].map((item) => (
              <div key={item.type} className="bg-white rounded-2xl border border-slate-200/80 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-base font-medium text-slate-900">{item.type}</p>
                    <p className="text-[13px] text-slate-500">{item.desc}</p>
                  </div>
                  <p className="font-mono text-sm font-semibold text-slate-900 whitespace-nowrap">{item.range}</p>
                </div>
                <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full transition-all" style={{ width: item.width }}></div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 italic">Prices approximate based on recent market data. Always verify with a local professional.</p>
        </section>

        {/* ─── CTA 1 ─── */}
        <section className="py-12 border-b border-slate-100">
          <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Not sure if your budget is realistic?</h3>
              <p className="text-sm text-slate-500">Set your actual min–max and see exactly which areas and property types are in play.</p>
            </div>
            <Link to="/blueprint" className="flex-shrink-0" data-testid="budget-cta-1">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                Check My Budget Range <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* ─── What You Get by Price Band ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
              What each price band <span className="italic">actually gets you</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed mb-8">
              The same budget buys very different things depending on where you look. Here's the breakdown.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {[
              {
                band: '$400K – $700K',
                title: 'Entry point',
                gets: ['1–2 bed condo in Downtown or Burlington East', 'Some townhomes in Tyandaga or Burlington East', 'Parking included in most buildings'],
                tradeoff: 'Limited to condos/towns in specific areas. No detached options.',
                accent: 'border-l-4 border-l-blue-400',
              },
              {
                band: '$700K – $1M',
                title: 'The sweet spot for first movers',
                gets: ['Townhomes in most neighbourhoods', 'Smaller detached in Burlington East, Tyandaga, Kilbride', 'Some freehold options with yards'],
                tradeoff: 'Detached options older or in further-out areas. Competitive segment.',
                accent: 'border-l-4 border-l-emerald-400',
              },
              {
                band: '$1M – $1.5M',
                title: 'Family upgrade territory',
                gets: ['Detached 3-4 bed in Orchard, Millcroft, LaSalle, Freeman', 'Larger lots, finished basements, double garages', 'Strong school catchments in most of these areas'],
                tradeoff: 'Premium areas like Roseland start at the top of this range.',
                accent: 'border-l-4 border-l-orange-400',
              },
              {
                band: '$1.5M+',
                title: 'Premium & waterfront',
                gets: ['Large lots in Roseland, Alton Village premium', 'Waterfront access in Shoreacres ($2M+)', 'Custom builds and estate properties'],
                tradeoff: 'Inventory is thin. Homes sell fast. Patience or flexibility required.',
                accent: 'border-l-4 border-l-amber-400',
              },
            ].map((item, i) => (
              <div key={i} className={`bg-white rounded-2xl border border-slate-200/80 p-6 ${item.accent}`}>
                <p className="font-mono text-xs text-slate-400 mb-1">{item.band}</p>
                <p className="text-lg font-medium text-slate-900 mb-3" style={{ fontFamily: "'Instrument Serif', serif" }}>{item.title}</p>
                <div className="space-y-2 mb-4">
                  {item.gets.map((g, j) => (
                    <div key={j} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5"></div>
                      <span className="text-[13px] text-slate-600">{g}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-3 border-t border-slate-100">
                  <p className="text-[12px] text-slate-400 italic">{item.tradeoff}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── CTA 2 ─── */}
        <section className="py-12 border-b border-slate-100">
          <div className="bg-slate-50 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl tracking-tight mb-1" style={{ fontFamily: "'Instrument Serif', serif" }}>Want to know exactly which areas match your number?</h3>
              <p className="text-sm text-slate-500">The Blueprint scores all 21 neighbourhoods against your actual budget range.</p>
            </div>
            <Link to="/blueprint" className="flex-shrink-0" data-testid="budget-cta-2">
              <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-6 h-10 text-sm font-medium shadow-lg shadow-slate-900/20 hover:-translate-y-px transition-all whitespace-nowrap">
                Score My Budget Fit <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>

        {/* ─── Toronto Comparison ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Burlington vs. Toronto: <span className="italic">what changes</span>
            </h2>
            <p className="text-base text-slate-500 leading-relaxed mb-8">
              The same $1.2M budget looks completely different in Burlington vs. Toronto.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-2xl border border-slate-200/80 p-6">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-slate-400 mb-3">$1.2M in Toronto</p>
              <div className="space-y-2">
                {['2-bed condo downtown or small semi in east end', 'Shared outdoor space (if any)', 'Parking costs extra', 'Schools may require lottery entry', 'Walk to transit — but pay for the privilege'].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0 mt-1.5"></div>
                    <span className="text-[13px] text-slate-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-emerald-50/50 rounded-2xl border border-emerald-100/50 p-6">
              <p className="text-[10px] font-semibold tracking-widest uppercase text-emerald-600 mb-3">$1.2M in Burlington</p>
              <div className="space-y-2">
                {['3–4 bed detached with yard and garage', 'Family-friendly street in a strong school zone', 'Driveway + often finished basement', 'Burlington GO to Union in 40 min', 'Parks, trails, and lake access nearby'].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0 mt-1.5"></div>
                    <span className="text-[13px] text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-slate-900 rounded-2xl p-6 max-w-3xl">
            <p className="text-base text-white leading-relaxed" style={{ fontFamily: "'Instrument Serif', serif" }}>
              The question isn't "Is Burlington cheaper?" It's: <span className="italic text-emerald-400">Is the space, lifestyle, and school access worth a slightly different commute?</span>
            </p>
          </div>
        </section>

        {/* ─── Common Mistakes ─── */}
        <section className="py-16 md:py-24 border-b border-slate-100">
          <div className="max-w-3xl">
            <h2 className="text-3xl md:text-4xl tracking-tight mb-6" style={{ fontFamily: "'Instrument Serif', serif" }}>
              Budget mistakes <span className="italic">Burlington buyers make</span>
            </h2>
            <div className="space-y-4">
              {[
                { num: '01', title: 'Searching by price alone', text: 'A $1M home in Kilbride is completely different from a $1M home in Downtown. The neighbourhood context matters as much as the number.' },
                { num: '02', title: 'Ignoring property type alignment', text: 'If you need detached and your budget is $650K, you need to know that — before you fall in love with a neighbourhood that only has $1.2M detached.' },
                { num: '03', title: 'Not factoring in commute costs', text: 'A cheaper home further out may cost more in gas, GO passes, and time. Factor in the total monthly picture.' },
                { num: '04', title: 'Comparing Toronto prices directly', text: 'Burlington pricing logic is different. Location premiums, lot sizes, and price-per-square-foot ratios all shift.' },
              ].map((item) => (
                <div key={item.num} className="flex gap-4 p-4 bg-white rounded-2xl border border-slate-200/80">
                  <span className="font-mono text-xs text-slate-400 mt-0.5">{item.num}</span>
                  <div>
                    <p className="text-sm font-medium text-slate-900 mb-1">{item.title}</p>
                    <p className="text-[13px] text-slate-500 leading-relaxed">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>

      {/* ─── Final CTA ─── */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="relative rounded-3xl overflow-hidden p-12 md:p-16">
            <div className="absolute inset-0">
              <img src="https://customer-assets.emergentagent.com/job_prequalify-leads-1/artifacts/yr1apojk_shutterstock_2665884737.jpg" alt="Burlington waterfront" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-slate-900/75" />
            </div>
            <div className="relative z-10 max-w-xl">
              <h2 className="text-3xl md:text-4xl text-white tracking-tight leading-[1.15] mb-4" style={{ fontFamily: "'Instrument Serif', serif" }}>
                Stop guessing. <span className="italic">See what your budget unlocks.</span>
              </h2>
              <p className="text-base text-white/50 leading-relaxed mb-2">Set your actual budget range in the Buyer Blueprint and get:</p>
              <div className="space-y-2 mb-8">
                {[
                  'All 21 neighbourhoods scored against your price range',
                  'Property type alignment by area',
                  'Trade-off visibility — what you gain and what you give up',
                  'Clear next steps based on your readiness',
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0"></div>
                    <span className="text-sm text-white/70">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-white/30 mb-6">Under 3 minutes. Free. No realtor call required.</p>
              <Link to="/blueprint" data-testid="budget-cta">
                <Button className="bg-white text-slate-900 hover:bg-slate-100 rounded-full px-7 py-3 h-auto text-sm font-semibold hover:-translate-y-px transition-all shadow-xl">
                  Start My Budget Check <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-sm text-slate-400">Prices are approximate and based on recent market activity. Always verify with a licensed professional.</p>
        </div>
      </section>
    </div>
  );
}
